import { useMemo } from 'react'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined'
import TrendingFlatOutlinedIcon from '@mui/icons-material/TrendingFlatOutlined'
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts'
import Tooltip from '../common/Tooltip'
import anonymizeCompanies from '../../helpers/dataHelpers/anonymizeCompanies'

// Growth is a % for MSI-effect benchmarks and a treden count for the
// participatieladder benchmark — `unit` ('%' by default) drives all labels.
const fmtGrowth = (v, unit) => `${v > 0 ? '+' : ''}${v}${unit === '%' ? '%' : ` ${unit}`}`

// Small hash → stable jitter per companyId so overlapping dots don't stack.
const jitterFor = (companyId) => {
    let hash = 0
    for (let i = 0; i < companyId.length; i++) {
        hash = (hash * 31 + companyId.charCodeAt(i)) | 0
    }
    // Map to [-0.35, 0.35]
    return ((Math.abs(hash) % 71) - 35) / 100
}

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null
    const row = payload[0].payload
    return (
        <div className='bd-dot-tooltip'>
            <strong>{row.label}</strong>
            <div>{fmtGrowth(row.growth, row.unit || '%')} groei</div>
            <div className='bd-dot-tooltip-sub'>#{row.rank} van {row.totalCompanies}</div>
        </div>
    )
}

const CustomDot = (props) => {
    const { cx, cy, payload } = props
    if (cx == null || cy == null) return null
    if (payload.isCurrent) {
        return (
            <g>
                <circle cx={cx} cy={cy} r={9} fill='#206c7f' stroke='#fff' strokeWidth={2} />
                <circle cx={cx} cy={cy} r={14} fill='none' stroke='#206c7f' strokeOpacity={0.3} strokeWidth={2} />
            </g>
        )
    }
    return <circle cx={cx} cy={cy} r={6} fill='#47acc3' stroke='#fff' strokeWidth={1} />
}

/**
 * Label per organisatie in de spreiding. Standaard anoniem ('Organisatie A',
 * 'Uw organisatie'); alleen een meegegeven namenlijst — de beheerweergave —
 * vervangt die door de echte naam.
 *
 * @param {Array<{companyId: string}>} distribution
 * @param {string|null} currentCompanyId
 * @param {Map<string, string>|null} [companyNames]
 * @returns {Map<string, string>}
 */
export const distributionLabels = (distribution, currentCompanyId, companyNames = null) => {
    const anoniem = anonymizeCompanies(distribution, currentCompanyId)
    if (!companyNames) return anoniem
    return new Map(distribution.map((row) => [
        row.companyId,
        companyNames.get(row.companyId) || anoniem.get(row.companyId),
    ]))
}

/**
 * Scatterplot van de organisaties in een benchmark: één stip per organisatie op
 * de groei-as, met het gemiddelde als stippellijn. Ook gebruikt op het
 * themadetail van Intelligence, waar geen eigen organisatie in zit — dan is er
 * ook geen legenda voor 'Uw organisatie'.
 *
 * @param {object} props
 * @param {object | null} props.positioning - msiBenchmark-shaped positioning block (distribution + currentCompany)
 * @param {number | null} props.benchmarkGrowth - The benchmark mean, in `unit`
 * @param {string} [props.unit] - '%' (MSI effects, default) or a word like 'treden' (participatieladder)
 * @param {Map<string, string>|null} [props.companyNames] - Alleen voor het beheerteam: echte namen in plaats van anonieme labels
 */
export const DistributionDotPlot = ({ positioning, benchmarkGrowth, unit = '%', companyNames = null }) => {
    const distribution = useMemo(
        () => positioning?.distribution || [],
        [positioning]
    )

    const chartData = useMemo(() => {
        if (distribution.length === 0) return []
        const currentId = positioning?.currentCompany?.companyId || null
        const labelMap = distributionLabels(distribution, currentId, companyNames)
        const total = distribution.length

        return distribution.map((row) => ({
            companyId: row.companyId,
            growth: row.growth,
            rank: row.rank,
            totalCompanies: total,
            isCurrent: row.isCurrent,
            label: labelMap.get(row.companyId) || row.companyId,
            y: jitterFor(row.companyId),
            unit,
        }))
    }, [distribution, positioning, unit, companyNames])

    if (distribution.length === 0) return null

    // Axis domain: round out to the nearest step with a little padding.
    // %-growth reads in steps of 5; treden read in steps of 1.
    const step = unit === '%' ? 5 : 1
    const growths = distribution.map(d => d.growth)
    const minG = Math.min(...growths, benchmarkGrowth ?? 0)
    const maxG = Math.max(...growths, benchmarkGrowth ?? 0)
    const pad = Math.max(step, Math.round((maxG - minG) * 0.1))
    const domainMin = Math.floor((minG - pad) / step) * step
    const domainMax = Math.ceil((maxG + pad) / step) * step

    return (
        <div className='bd-dot-plot-section' role='figure' aria-label='Spreiding van de organisaties in de benchmark'>

            <div className='bd-dot-plot-chart'>
                <ResponsiveContainer width='100%' height={220}>
                    <ScatterChart margin={{ top: 20, right: 30, bottom: 30, left: 30 }}>
                        <CartesianGrid strokeDasharray='3 3' vertical={false} />
                        <XAxis
                            type='number'
                            dataKey='growth'
                            domain={[domainMin, domainMax]}
                            tickFormatter={(v) => unit === '%' ? `${v}%` : `${v}`}
                            label={{ value: unit === '%' ? 'Groei' : `Groei (${unit})`, position: 'insideBottom', offset: -10 }}
                        />
                        <YAxis
                            type='number'
                            dataKey='y'
                            domain={[-1, 1]}
                            hide
                        />
                        <RechartsTooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                        {benchmarkGrowth != null && (
                            <ReferenceLine
                                x={benchmarkGrowth}
                                stroke='#6c7a89'
                                strokeDasharray='4 4'
                                label={{ value: `Gemiddelde ${unit === '%' ? `${benchmarkGrowth}%` : `${benchmarkGrowth} ${unit}`}`, position: 'top', fill: '#6c7a89', fontSize: 11 }}
                            />
                        )}
                        <Scatter data={chartData} shape={<CustomDot />} />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>

            {/* Zonder eigen organisatie is er één soort stip, en dan geen
                legenda: 'Uw organisatie' zou een stip benoemen die er niet is. */}
            {positioning?.currentCompany && (
                <div className='bd-dot-plot-legend'>
                    <span className='bd-dot-plot-legend-item'>
                        <span className='bd-dot-plot-swatch bd-dot-plot-swatch--current' />
                        Uw organisatie
                    </span>
                    <span className='bd-dot-plot-legend-item'>
                        <span className='bd-dot-plot-swatch bd-dot-plot-swatch--other' />
                        Andere organisaties
                    </span>
                </div>
            )}
        </div>
    )
}

/**
 * Combined "Where do you stand?" card — stats on top, distribution plot below.
 *
 * @param {object} props
 * @param {object | null} props.positioning - msiBenchmark-shaped positioning block (distribution + currentCompany)
 * @param {number | null} props.benchmarkGrowth - The benchmark mean, in `unit`
 * @param {string} [props.unit] - '%' (MSI effects, default) or a word like 'treden' (participatieladder)
 */
const BenchmarkPositionBlock = ({ positioning, benchmarkGrowth, unit = '%' }) => {
    if (!positioning) return null

    const current = positioning.currentCompany

    if (!current) {
        return (
            <div className='bd-position-block bd-position-block--empty'>
                <h2 className='bd-section-heading'>Waar sta je?</h2>
                <p>Jullie organisatie heeft nog geen data voor dit effect. Zodra er twee meetmomenten zijn ingevuld, verschijnt jullie positie hier.</p>
            </div>
        )
    }

    const { growth, rank, totalCompanies, percentile, deltaVsBenchmark } = current

    const percentileLabel = percentile >= 75
        ? `Top ${100 - percentile + Math.round(100 / totalCompanies)}%`
        : percentile <= 25
            ? `Onderste ${percentile}%`
            : 'Middenmoot'

    const deltaTooltipContent = deltaVsBenchmark == null
        ? 'Geen vergelijking beschikbaar'
        : deltaVsBenchmark > 0
            ? 'Boven de benchmark'
            : deltaVsBenchmark < 0
                ? 'Onder de benchmark'
                : 'Gelijk aan de benchmark'

    const deltaIcon = (
        <Tooltip content={deltaTooltipContent} top='30px'>
            {deltaVsBenchmark == null
                ? <TrendingFlatOutlinedIcon />
                : deltaVsBenchmark > 0
                    ? <TrendingUpOutlinedIcon />
                    : deltaVsBenchmark < 0
                        ? <TrendingDownOutlinedIcon />
                        : <TrendingFlatOutlinedIcon />}
        </Tooltip>
    )

    const deltaClass = deltaVsBenchmark == null
        ? 'bd-delta--neutral'
        : deltaVsBenchmark > 0
            ? 'bd-delta--positive'
            : deltaVsBenchmark < 0
                ? 'bd-delta--negative'
                : 'bd-delta--neutral'

    const deltaLabel = deltaVsBenchmark == null
        ? '—'
        : `${fmtGrowth(deltaVsBenchmark, unit)} ${deltaVsBenchmark >= 0 ? 'boven' : 'onder'} benchmark`

    return (
        <div className='bd-position-block'>
            <h2 className='bd-section-heading'>Waar sta je?</h2>

            <div className='bd-position-stats'>
                <div className='bd-position-stat'>
                    <span className='bd-position-stat-label'>Jullie groei</span>
                    <span className='bd-position-stat-value bd-position-stat-value--own'>
                        {fmtGrowth(growth, unit)}
                    </span>
                </div>

                <div className='bd-position-stat'>
                    <span className='bd-position-stat-label'>Positie</span>
                    <span className='bd-position-stat-value'>#{rank} van {totalCompanies}</span>
                </div>

                <div className='bd-position-stat'>
                    <span className='bd-position-stat-label'>t.o.v. benchmark</span>
                    <span className={`bd-position-stat-value bd-delta ${deltaClass}`}>
                        {deltaLabel}
                    </span>
                    {benchmarkGrowth != null && (
                        <span className='bd-position-stat-sub'>benchmark: {fmtGrowth(benchmarkGrowth, unit)}</span>
                    )}
                </div>
            </div>

            <DistributionDotPlot
                positioning={positioning}
                benchmarkGrowth={benchmarkGrowth}
                unit={unit}
            />
        </div>
    )
}

export default BenchmarkPositionBlock
